import { nanoid } from 'nanoid';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AUTHORIZATION_HEADER_VALUE_PREFIX } from '@/core/constants/auth';
import { useAuthStore } from '@/core/store/auth/authStore';

import { useDeleteDcrAttachmentMutation } from '../_graphql/mutation/DeleteDcrAttachment.hook';
import {
	ATTACHMENT_ACCEPTED_EXTENSIONS,
	ATTACHMENT_ACCEPTED_TYPES,
	ATTACHMENT_MAX_FILES,
	ATTACHMENT_MAX_SIZE_BYTES,
	DcrAttachment
} from '../_types/attachment';

type ValidationResult = { ok: true } | { ok: false; error: string };

export type AddFilesResult = {
	added: number;
	rejected: { file: File; error: string }[];
};

type UploadResponse = {
	id: string;
	file_name: string;
	file_content_type: string;
	file_size: number;
	view_url: string;
};

type Args = {
	attachments: DcrAttachment[];
	onChange: (next: DcrAttachment[]) => void;
	sessionId: string;
};

const UPLOAD_PATH = '/dcr/attachments';

export const useAttachmentUpload = ({
	attachments,
	onChange,
	sessionId
}: Args) => {
	const { t } = useTranslation();
	const xhrsRef = useRef<Map<string, XMLHttpRequest>>(new Map());
	const previewUrlsRef = useRef<Set<string>>(new Set());
	const attachmentsRef = useRef(attachments);

	const [deleteDcrAttachmentMutation] = useDeleteDcrAttachmentMutation();

	useEffect(() => {
		attachmentsRef.current = attachments;
	}, [attachments]);

	useEffect(() => {
		const xhrs = xhrsRef.current;
		const previewUrls = previewUrlsRef.current;

		return () => {
			xhrs.forEach(xhr => xhr.abort());
			xhrs.clear();

			previewUrls.forEach(url => URL.revokeObjectURL(url));
			previewUrls.clear();
		};
	}, []);

	const validateFile = useCallback(
		(file: File): ValidationResult => {
			const mimeOk = ATTACHMENT_ACCEPTED_TYPES.includes(file.type);
			const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
			const extOk = ATTACHMENT_ACCEPTED_EXTENSIONS.includes(ext);

			if (!mimeOk && !extOk) {
				return { ok: false, error: t('Unsupported file type. Images only.') };
			}

			if (file.size > ATTACHMENT_MAX_SIZE_BYTES) {
				return { ok: false, error: t('File exceeds 10MB limit.') };
			}

			return { ok: true };
		},
		[t]
	);

	const updateAttachment = useCallback(
		(id: string, patch: Partial<DcrAttachment>) => {
			const next = attachmentsRef.current.map(att =>
				att.id === id ? { ...att, ...patch } : att
			);

			attachmentsRef.current = next;
			onChange(next);
		},
		[onChange]
	);

	const startUpload = useCallback(
		(localId: string, file: File) => {
			const baseUrl = import.meta.env.VITE_ODCO_API_BASE_URL;
			const url = `${baseUrl}${UPLOAD_PATH}`;

			const formData = new FormData();
			formData.append('file', file);
			formData.append('session_id', sessionId);

			const xhr = new XMLHttpRequest();
			xhr.open('POST', url, true);
			xhr.withCredentials = true;

			const token = useAuthStore.getState().idToken;

			if (token) {
				xhr.setRequestHeader(
					'Authorization',
					`${AUTHORIZATION_HEADER_VALUE_PREFIX} ${token}`
				);
			}

			xhr.upload.onprogress = e => {
				if (!e.lengthComputable) return;

				const progress = Math.min(100, Math.round((e.loaded / e.total) * 100));
				updateAttachment(localId, { progress });
			};

			xhr.onload = () => {
				xhrsRef.current.delete(localId);

				if (xhr.status >= 200 && xhr.status < 300) {
					try {
						const response = JSON.parse(xhr.responseText) as UploadResponse;

						updateAttachment(localId, {
							progress: 100,
							status: 'success',
							remoteId: response.id
						});
					} catch {
						updateAttachment(localId, {
							status: 'error',
							error: t('Upload failed.')
						});
					}
				} else {
					updateAttachment(localId, {
						status: 'error',
						error: t('Upload failed.')
					});
				}
			};

			xhr.onerror = () => {
				xhrsRef.current.delete(localId);
				updateAttachment(localId, {
					status: 'error',
					error: t('Upload failed.')
				});
			};

			xhr.onabort = () => {
				xhrsRef.current.delete(localId);
			};

			xhrsRef.current.set(localId, xhr);
			xhr.send(formData);
		},
		[sessionId, updateAttachment, t]
	);

	const addFiles = useCallback(
		(files: File[]): AddFilesResult => {
			const slotsLeft = Math.max(
				0,
				ATTACHMENT_MAX_FILES - attachmentsRef.current.length
			);

			const rejected: AddFilesResult['rejected'] = [];
			const accepted: File[] = [];

			files.forEach(file => {
				const result = validateFile(file);

				if (!result.ok) {
					rejected.push({ file, error: result.error });
					return;
				}

				if (accepted.length >= slotsLeft) {
					rejected.push({
						file,
						error: t('Maximum of {{max}} attachments reached.', {
							max: ATTACHMENT_MAX_FILES
						})
					});
					return;
				}

				accepted.push(file);
			});

			const newAttachments: DcrAttachment[] = accepted.map(file => {
				const id = nanoid();
				const previewUrl = URL.createObjectURL(file);
				previewUrlsRef.current.add(previewUrl);

				return {
					id,
					name: file.name,
					size: file.size,
					mimeType: file.type,
					previewUrl,
					status: 'uploading',
					progress: 0
				};
			});

			if (newAttachments.length > 0) {
				const next = [...attachmentsRef.current, ...newAttachments];

				attachmentsRef.current = next;
				onChange(next);

				newAttachments.forEach((att, idx) =>
					startUpload(att.id, accepted[idx])
				);
			}

			return { added: newAttachments.length, rejected };
		},
		[validateFile, onChange, startUpload, t]
	);

	const removeAttachment = useCallback(
		(id: string) => {
			const xhr = xhrsRef.current.get(id);
			if (xhr) {
				xhr.abort();
				xhrsRef.current.delete(id);
			}

			const target = attachmentsRef.current.find(att => att.id === id);
			if (target?.previewUrl) {
				URL.revokeObjectURL(target.previewUrl);
				previewUrlsRef.current.delete(target.previewUrl);
			}

			// Fire-and-forget: removal from UI is instant regardless of API result.
			if (target?.remoteId) {
				deleteDcrAttachmentMutation({
					variables: { attachmentId: target.remoteId }
				}).catch(() => {
					// Intentionally swallow to prevent unhandled error.
				});
			}

			const next = attachmentsRef.current.filter(att => att.id !== id);
			attachmentsRef.current = next;
			onChange(next);
		},
		[onChange, deleteDcrAttachmentMutation]
	);

	return { addFiles, removeAttachment, validateFile };
};
